import React, { Suspense, lazy } from 'react';
import type { CustomField } from '@puckeditor/core';

export type BrandPickerFieldProps = {
  field: { label?: string };
  name: string;
  readOnly?: boolean;
};

// Lazy so `@apollo/client`, the generated brand-search document, and `ReactSelect` stay out of the
// storefront bundle. `carousel-types.ts` (and through it `product-carousel.tsx`) is reachable from
// the production renderer, so a static import of the widget would ship an Apollo client to every
// shopper who views a published CMS page even though only the editor can ever render this field.
// Mirrors `asset-picker-control.tsx`'s lazy boundary; `bundle-boundaries.test.ts`'s static-only
// walk is what actually enforces that this stays dynamic.
const BrandPickerWidget = lazy(() => import('./brand-picker-widget'));

function BrandPickerFieldRender(props: BrandPickerFieldProps): JSX.Element {
  return (
    <Suspense fallback={null}>
      <BrandPickerWidget {...props} />
    </Suspense>
  );
}

/**
 * Puck `custom` field that renders an async brand typeahead.
 *
 * Bound to the `brandId` prop. On selection the widget also updates `brandName`
 * via a Puck replace action so heading derivation is synchronous — no extra
 * query on render needed.
 *
 * `ai.exclude` is set because the AI plugin cannot meaningfully pick a brand
 * by querying a live search endpoint; authors must choose brands manually.
 */
export const brandPickerField: CustomField<string> = {
  type: 'custom',
  label: 'Brand',
  render: BrandPickerFieldRender as CustomField<string>['render'],
  ai: { exclude: true },
};
